import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Book,
  Webhook,
  Key,
  Lock,
  Code as CodeIcon,
  Copy,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API_BASE = "https://attestrasign.com/api";

function CodeBlock({ language = "bash", children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copied");
  };
  return (
    <div className="relative group rounded-lg border border-slate-800 bg-slate-950 text-slate-100 my-3 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
        <span>{language}</span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-slate-400 hover:text-white"
        >
          {copied ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <pre className="px-4 py-3 overflow-x-auto text-[12.5px] leading-relaxed">
        <code>{children.trim()}</code>
      </pre>
    </div>
  );
}

function Endpoint({ method, path, children }) {
  const colors = {
    GET: "bg-blue-50 text-blue-700 border-blue-200",
    POST: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PATCH: "bg-amber-50 text-amber-700 border-amber-200",
    DELETE: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 my-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[11px] font-mono font-bold px-1.5 py-0.5 rounded border ${
            colors[method] || "bg-slate-50 text-slate-700 border-slate-200"
          }`}
        >
          {method}
        </span>
        <span className="font-mono text-sm break-all">{path}</span>
      </div>
      <div className="mt-2 text-xs text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

export default function ApiDocs() {
  useEffect(() => {
    document.title = "Attestra · API & Webhooks docs";
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl mx-auto" data-testid="api-docs-page">
      <Link
        to="/app/settings"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Settings
      </Link>
      <div className="mt-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-emerald-50 flex items-center justify-center">
          <Book className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Integrations docs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Use the Attestra REST API + webhooks to pull document state into your CRM,
            HRIS, or any internal workflow.
          </p>
        </div>
      </div>

      {/* Quick info row */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Base URL
          </div>
          <div className="mt-1 font-mono text-xs break-all">{API_BASE}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Auth
          </div>
          <div className="mt-1 font-mono text-xs">Authorization: Bearer attst_…</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Format
          </div>
          <div className="mt-1 font-mono text-xs">application/json · UTF-8</div>
        </div>
      </div>

      <Tabs defaultValue="quickstart" className="mt-8">
        <div className="-mx-4 sm:mx-0 overflow-x-auto px-4 sm:px-0">
          <TabsList className="w-max">
            <TabsTrigger value="quickstart">
              <Key className="h-3.5 w-3.5 mr-1.5" /> Quickstart
            </TabsTrigger>
            <TabsTrigger value="rest">
              <CodeIcon className="h-3.5 w-3.5 mr-1.5" /> REST endpoints
            </TabsTrigger>
            <TabsTrigger value="webhooks">
              <Webhook className="h-3.5 w-3.5 mr-1.5" /> Webhooks
            </TabsTrigger>
            <TabsTrigger value="verify">
              <Lock className="h-3.5 w-3.5 mr-1.5" /> Verify signatures
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Quickstart */}
        <TabsContent value="quickstart" className="mt-4">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">1. Create an API token</h2>
            <p className="text-sm text-slate-600">
              Open{" "}
              <Link
                to="/app/settings"
                className="text-emerald-700 hover:underline font-medium"
              >
                Settings → API & Webhooks
              </Link>{" "}
              and create a token. Save the value the moment it's shown — Attestra hashes
              it at rest and cannot show it again.
            </p>
            <h2 className="text-lg font-semibold mt-6">2. Make your first call</h2>
            <p className="text-sm text-slate-600">
              List the documents in your workspace:
            </p>
            <CodeBlock language="bash">{`curl ${API_BASE}/documents \\
  -H "Authorization: Bearer attst_YOUR_TOKEN_HERE"`}</CodeBlock>

            <h2 className="text-lg font-semibold mt-6">3. Subscribe to a webhook</h2>
            <p className="text-sm text-slate-600">
              Or do the same via the UI — Settings → API & Webhooks → Add webhook.
            </p>
            <CodeBlock language="bash">{`curl -X POST ${API_BASE}/webhooks \\
  -H "Authorization: Bearer attst_YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-app.com/webhooks/attestra",
    "events": ["document.completed", "document.declined"]
  }'`}</CodeBlock>
          </section>
        </TabsContent>

        {/* REST */}
        <TabsContent value="rest" className="mt-4">
          <section>
            <h2 className="text-lg font-semibold mb-2">Documents</h2>
            <Endpoint method="GET" path="/api/documents">
              List documents in the caller's workspace. Optional query params:{" "}
              <code>status</code>, <code>folder</code>, <code>tag</code>,{" "}
              <code>department_id</code>, <code>q</code>.
            </Endpoint>
            <Endpoint method="GET" path="/api/documents/{id}">
              Retrieve a single document with recipients, fields, and audit trail.
            </Endpoint>
            <Endpoint method="POST" path="/api/documents">
              Create a draft document from raw fields. For most flows, prefer creating
              from a template via <code>POST /api/templates/{"{id}"}/use</code>.
            </Endpoint>
            <Endpoint method="PATCH" path="/api/documents/{id}">
              Update title, recipients, fields, signing mode, expiry, or message on a
              draft.
            </Endpoint>
            <Endpoint method="POST" path="/api/documents/{id}/send">
              Send the document — fires signing-link emails and emits the{" "}
              <code>document.sent</code> webhook event.
            </Endpoint>
            <Endpoint method="POST" path="/api/documents/{id}/remind/{recipient_id}">
              Email a reminder to a single recipient who hasn't signed yet.
            </Endpoint>
            <Endpoint method="GET" path="/api/documents/{id}/download/signed.pdf">
              Download the signed PDF with burnt-in signatures + certificate page.
            </Endpoint>
            <Endpoint method="GET" path="/api/documents/{id}/download/certificate.pdf">
              Download just the certificate of completion.
            </Endpoint>

            <h2 className="text-lg font-semibold mt-8 mb-2">Templates</h2>
            <Endpoint method="GET" path="/api/templates">
              List templates. Query params: <code>category</code>, <code>archived</code>
              , <code>q</code>.
            </Endpoint>
            <Endpoint method="POST" path="/api/templates/{id}/use">
              Spin up a new draft document from a template. Body:{" "}
              <code>{`{ title?, folder?, department_id? }`}</code>.
            </Endpoint>

            <h2 className="text-lg font-semibold mt-8 mb-2">Tokens & webhooks</h2>
            <Endpoint method="GET" path="/api/api-tokens">
              List your API tokens (no secret values returned).
            </Endpoint>
            <Endpoint method="POST" path="/api/api-tokens">
              Create a token. <strong>The full token is only returned once.</strong>
            </Endpoint>
            <Endpoint method="DELETE" path="/api/api-tokens/{id}">
              Revoke a token. The integration using it stops working immediately.
            </Endpoint>
            <Endpoint method="GET" path="/api/webhooks">
              List webhook subscriptions for your workspace.
            </Endpoint>
            <Endpoint method="POST" path="/api/webhooks">
              Subscribe. Body:{" "}
              <code>{`{ url, events: ["document.completed", ...], description? }`}</code>
              .
            </Endpoint>
            <Endpoint method="PATCH" path="/api/webhooks/{id}">
              Update URL, events, description, or <code>active</code>.
            </Endpoint>
            <Endpoint method="DELETE" path="/api/webhooks/{id}">
              Delete a subscription.
            </Endpoint>
            <Endpoint method="GET" path="/api/webhooks/{id}/deliveries">
              Recent delivery attempts (last 100), most recent first.
            </Endpoint>
            <Endpoint method="POST" path="/api/webhooks/{id}/test">
              Fire a test payload (event <code>webhook.test</code>) to verify your
              endpoint accepts and validates the signature.
            </Endpoint>
          </section>
        </TabsContent>

        {/* Webhooks */}
        <TabsContent value="webhooks" className="mt-4">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Events</h2>
            <p className="text-sm text-slate-600">
              Attestra emits seven event types. Subscribe to one or more when you
              create a webhook.
            </p>
            <ul className="text-sm font-mono text-slate-700 space-y-1 mt-3">
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">document.sent</code>
                <span className="font-sans text-slate-500 ml-2">
                  fired the moment a document is sent to recipients
                </span>
              </li>
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                  document.completed
                </code>
                <span className="font-sans text-slate-500 ml-2">
                  the last signer has signed
                </span>
              </li>
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                  document.declined
                </code>
                <span className="font-sans text-slate-500 ml-2">
                  any recipient declined
                </span>
              </li>
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                  document.expired
                </code>
                <span className="font-sans text-slate-500 ml-2">
                  document passed its expires_at
                </span>
              </li>
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                  document.sent_back
                </code>
                <span className="font-sans text-slate-500 ml-2">
                  an approver requested changes
                </span>
              </li>
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                  approval.granted
                </code>
                <span className="font-sans text-slate-500 ml-2">approver approved</span>
              </li>
              <li>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">
                  approval.rejected
                </code>
                <span className="font-sans text-slate-500 ml-2">approver rejected</span>
              </li>
            </ul>

            <h2 className="text-lg font-semibold mt-8">Payload shape</h2>
            <p className="text-sm text-slate-600">
              Every event POSTs the same envelope. <code>data.document</code> is a
              compact representation; fetch the full doc via{" "}
              <code>GET /api/documents/{"{id}"}</code> if you need fields or audit.
            </p>
            <CodeBlock language="json">{`{
  "event": "document.completed",
  "delivered_at": "2026-05-12T07:29:12+00:00",
  "data": {
    "id": "cc504866-…",
    "title": "Mutual NDA",
    "status": "completed",
    "signing_mode": "sequential",
    "completed_at": "2026-05-12T07:28:00+00:00",
    "hash": "5e1a…",
    "recipients": [
      {
        "id": "…",
        "name": "Priya Sharma",
        "email": "priya@acme.io",
        "role": "signer",
        "status": "signed",
        "signed_at": "2026-05-12T07:28:00+00:00"
      }
    ],
    "completed_by": "priya@acme.io"
  }
}`}</CodeBlock>

            <h2 className="text-lg font-semibold mt-8">Headers</h2>
            <CodeBlock language="http">{`Content-Type: application/json
User-Agent: Attestra-Webhooks/1.0
X-Attestra-Event: document.completed
X-Attestra-Delivery-Id: f4c3b2a1-…
X-Attestra-Signature: sha256=<hmac>`}</CodeBlock>

            <h2 className="text-lg font-semibold mt-8">Delivery & retries</h2>
            <p className="text-sm text-slate-600">
              Each delivery is a single POST with an 8-second timeout. We do not
              automatically retry failed deliveries today — open the webhook in
              Settings to inspect the last 100 delivery attempts (status code, error,
              duration). Always return <code>2xx</code> within 8 seconds; queue any
              heavy work in your own backend.
            </p>
          </section>
        </TabsContent>

        {/* Verify */}
        <TabsContent value="verify" className="mt-4">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Verifying signatures</h2>
            <p className="text-sm text-slate-600">
              The <code>X-Attestra-Signature</code> header carries an HMAC-SHA256 of
              the <strong>raw request body</strong> using your webhook's{" "}
              <code>whsec_…</code> secret. Always verify before trusting the payload.
            </p>

            <h3 className="font-medium mt-4">Node.js (Express)</h3>
            <CodeBlock language="javascript">{`import crypto from "node:crypto";

const WEBHOOK_SECRET = process.env.ATTESTRA_WEBHOOK_SECRET;

app.post(
  "/webhooks/attestra",
  express.raw({ type: "application/json" }), // need raw body for HMAC
  (req, res) => {
    const sig = (req.header("X-Attestra-Signature") || "").replace("sha256=", "");
    const expected = crypto
      .createHmac("sha256", WEBHOOK_SECRET)
      .update(req.body)
      .digest("hex");

    if (
      !crypto.timingSafeEqual(
        Buffer.from(sig, "hex"),
        Buffer.from(expected, "hex"),
      )
    ) {
      return res.status(401).send("Bad signature");
    }

    const event = JSON.parse(req.body.toString("utf8"));
    // ... handle event ...
    res.status(200).send("ok");
  },
);`}</CodeBlock>

            <h3 className="font-medium mt-4">Python (FastAPI)</h3>
            <CodeBlock language="python">{`import hmac, hashlib, os
from fastapi import FastAPI, Request, HTTPException

app = FastAPI()
WEBHOOK_SECRET = os.environ["ATTESTRA_WEBHOOK_SECRET"]

@app.post("/webhooks/attestra")
async def attestra_webhook(request: Request):
    raw = await request.body()
    sig = (request.headers.get("X-Attestra-Signature") or "").replace("sha256=", "")
    expected = hmac.new(
        WEBHOOK_SECRET.encode(), raw, hashlib.sha256
    ).hexdigest()

    if not hmac.compare_digest(sig, expected):
        raise HTTPException(status_code=401, detail="Bad signature")

    event = await request.json()
    # ... handle event ...
    return {"ok": True}`}</CodeBlock>
          </section>
        </TabsContent>
      </Tabs>

      <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="text-xs text-slate-500">
          Need an endpoint that's not listed? Ping support and we'll prioritize.
        </div>
        <Link to="/app/settings">
          <Button variant="outline">Manage tokens & webhooks</Button>
        </Link>
      </div>
    </div>
  );
}
